import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Form, Modal, Button, Table, Badge } from "react-bootstrap";
import { useParams } from "react-router-dom";

import {
  getEligibilityFiles,
  uploadEligibilityFileAdmin,
  getEligibilityFileLogs,
  retryEligibilityFile,
  getEmailTemplateSetting,
} from "../../api/axios";
import { showToast } from "../../Utills/toast";
import CmsCircularProgress from "../../Components/Common/CircularProgress";

const PAGE_SIZE = 20;
const DEFAULT_WELCOME_SLUG = "welcome-email-for-sftp";

const SYNC_OPTIONS = [
  { value: "", label: "All" },
  { value: "done", label: "Done" },
  { value: "failed", label: "Failed" },
  { value: "inprogress", label: "In Progress" },
  { value: "in-queue", label: "In Queue" },
];

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function syncBadge(sync) {
  switch (sync) {
    case "done":
      return "success";
    case "failed":
      return "danger";
    case "inprogress":
      return "warning";
    case "in-queue":
      return "info";
    default:
      return "secondary";
  }
}

function syncLabel(sync) {
  switch (sync) {
    case "done":
      return "Done";
    case "failed":
      return "Failed";
    case "inprogress":
      return "In Progress";
    case "in-queue":
      return "In Queue";
    default:
      return sync || "—";
  }
}

function UploadModal({ show, onHide, partnerId, onSuccess, templates }) {
  const [file, setFile] = useState(null);
  const [withHeaders, setWithHeaders] = useState(true);
  const [welcomeSlug, setWelcomeSlug] = useState(DEFAULT_WELCOME_SLUG);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    const hasDefault = templates.some((t) => t.slug === DEFAULT_WELCOME_SLUG);
    setWelcomeSlug(hasDefault ? DEFAULT_WELCOME_SLUG : templates[0]?.slug || DEFAULT_WELCOME_SLUG);
  }, [show, templates]);

  const reset = () => {
    setFile(null);
    setWithHeaders(true);
    setWelcomeSlug(DEFAULT_WELCOME_SLUG);
    setDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (!uploading) {
      reset();
      onHide();
    }
  };

  const pickFile = (next) => {
    if (!next) {
      setFile(null);
      return;
    }
    if (!next.name.toLowerCase().endsWith(".csv")) {
      showToast({ type: "error", message: "Please select a CSV file." });
      return;
    }
    setFile(next);
  };

  const handleSubmit = async () => {
    if (!file || !partnerId) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      showToast({ type: "error", message: "Please select a CSV file." });
      return;
    }
    if (!welcomeSlug) {
      showToast({ type: "error", message: "Please select a welcome email template." });
      return;
    }
    setUploading(true);
    try {
      await uploadEligibilityFileAdmin(partnerId, file, withHeaders, welcomeSlug);
      showToast({ type: "success", message: "File uploaded and queued for processing." });
      reset();
      onHide();
      onSuccess();
    } catch (err) {
      showToast({
        type: "error",
        message: err?.response?.data?.message || "Upload failed.",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <StyledModal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton={!uploading}>
        <Modal.Title>Upload eligibility file</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalIntro>
          Choose a partner CSV to queue for processing. Newly registered members
          will receive the welcome email you select below.
        </ModalIntro>

        <FieldBlock>
          <FieldLabel>CSV file</FieldLabel>
          <DropZone
            $active={dragOver}
            $hasFile={Boolean(file)}
            $disabled={uploading}
            onDragEnter={(e) => {
              e.preventDefault();
              if (!uploading) setDragOver(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (!uploading) setDragOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragOver(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (uploading) return;
              pickFile(e.dataTransfer.files?.[0] || null);
            }}
            onClick={() => {
              if (!uploading) fileInputRef.current?.click();
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (!uploading) fileInputRef.current?.click();
              }
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              disabled={uploading}
              hidden
              onChange={(e) => pickFile(e.target.files?.[0] || null)}
            />
            {file ? (
              <>
                <DropTitle>{file.name}</DropTitle>
                <DropHint>
                  {(file.size / 1024).toFixed(1)} KB · Click or drop to replace
                </DropHint>
              </>
            ) : (
              <>
                <DropTitle>Drop CSV here or click to browse</DropTitle>
                <DropHint>Only .csv files are accepted</DropHint>
              </>
            )}
          </DropZone>
        </FieldBlock>

        <FieldBlock>
          <FieldLabel htmlFor="welcome-email-template">Welcome email template</FieldLabel>
          <Form.Select
            id="welcome-email-template"
            value={welcomeSlug}
            disabled={uploading || templates.length === 0}
            onChange={(e) => setWelcomeSlug(e.target.value)}
          >
            {templates.length === 0 ? (
              <option value="">No templates found</option>
            ) : (
              templates.map((t) => (
                <option key={t.id || t.slug} value={t.slug}>
                  {t.name || t.slug}
                  {t.slug === DEFAULT_WELCOME_SLUG ? " (default)" : ""}
                </option>
              ))
            )}
          </Form.Select>
          <FieldHint>Sent to newly registered members from this file.</FieldHint>
        </FieldBlock>

        <OptionRow>
          <Form.Check
            type="switch"
            id="with-headers"
            label="First row contains headers"
            checked={withHeaders}
            disabled={uploading}
            onChange={(e) => setWithHeaders(e.target.checked)}
          />
        </OptionRow>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!file || !welcomeSlug || uploading}
        >
          {uploading ? "Uploading…" : "Upload"}
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
}

function LogsModal({ show, onHide, partnerId, fileId, fileName }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !fileId || !partnerId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getEligibilityFileLogs(partnerId, fileId);
        if (!cancelled) setLogs(res?.data?.data?.logs || []);
      } catch (err) {
        if (!cancelled) {
          showToast({
            type: "error",
            message: err?.response?.data?.message || "Failed to load logs.",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, fileId, partnerId]);

  const levelTone = (level) => {
    const value = String(level || "info").toLowerCase();
    if (value === "error" || value === "failed") return "error";
    if (value === "warn" || value === "warning") return "warn";
    if (value === "success" || value === "done") return "success";
    return "info";
  };

  return (
    <StyledModal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <div>
          <Modal.Title>Processing logs</Modal.Title>
          <LogsFileName title={fileName || fileId}>{fileName || fileId || "—"}</LogsFileName>
        </div>
      </Modal.Header>
      <Modal.Body style={{ paddingTop: 12 }}>
        {loading ? (
          <LogsEmptyState>
            <CmsCircularProgress />
          </LogsEmptyState>
        ) : logs.length === 0 ? (
          <LogsEmptyState>
            <LogsEmptyTitle>No logs yet</LogsEmptyTitle>
            <LogsEmptyHint>Events will appear here as this file is processed.</LogsEmptyHint>
          </LogsEmptyState>
        ) : (
          <LogsTableWrap>
            <LogsTableScroll>
              <LogsTable>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Level</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const tone = levelTone(log.level);
                    return (
                      <tr key={log.id}>
                        <td className="time">{formatDate(log.created)}</td>
                        <td>
                          <LevelBadge $tone={tone}>{log.level || "info"}</LevelBadge>
                        </td>
                        <td className="message">{log.message}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </LogsTable>
            </LogsTableScroll>
          </LogsTableWrap>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
}

export default function EligibilityFiles() {
  const { partnerId } = useParams();
  const [files, setFiles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsFile, setLogsFile] = useState({ id: null, name: "" });
  const [retryingId, setRetryingId] = useState(null);

  const templateNameBySlug = useCallback(
    (slug) => {
      if (!slug) return "—";
      const match = templates.find((t) => t.slug === slug);
      return match?.name || slug;
    },
    [templates],
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status, partnerId]);

  useEffect(() => {
    if (!partnerId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getEmailTemplateSetting(partnerId);
        const list = res?.data?.data || [];
        const active = (Array.isArray(list) ? list : []).filter(
          (t) => t.is_active === 1 || t.is_active === true || t.is_active === "1",
        );
        if (!cancelled) setTemplates(active.length ? active : list);
      } catch {
        if (!cancelled) setTemplates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  const loadFiles = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const res = await getEligibilityFiles(partnerId, {
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        status: status || undefined,
        sortBy: "created",
        sortOrder: "desc",
      });
      setFiles(res?.data?.data?.files || []);
      setTotalPages(res?.data?.data?.totalPages || 1);
    } catch (err) {
      showToast({
        type: "error",
        message: err?.response?.data?.message || "Failed to load eligibility files.",
      });
    } finally {
      setLoading(false);
    }
  }, [partnerId, page, debouncedSearch, status]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleRetry = async (row) => {
    if (row.sync !== "failed") return;
    setRetryingId(row.id);
    try {
      await retryEligibilityFile(partnerId, row.id);
      showToast({ type: "success", message: "File queued for retry." });
      loadFiles();
    } catch (err) {
      showToast({
        type: "error",
        message: err?.response?.data?.message || "Retry failed.",
      });
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <PageWrap>
      <HeaderRow>
        <div>
          <h2>Files</h2>
          <p>Upload partner eligibility CSVs and track processing status.</p>
        </div>
        <HeaderActions>
          <Button variant="outline-secondary" size="sm" onClick={loadFiles} disabled={loading}>
            Refresh
          </Button>
          <Button variant="primary" size="sm" onClick={() => setUploadOpen(true)}>
            Upload CSV
          </Button>
        </HeaderActions>
      </HeaderRow>

      <FilterBar>
        <FilterField>
          <Form.Label>Search</Form.Label>
          <Form.Control
            size="sm"
            placeholder="File name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </FilterField>
        <FilterField $narrow>
          <Form.Label>Status</Form.Label>
          <Form.Select size="sm" value={status} onChange={(e) => setStatus(e.target.value)}>
            {SYNC_OPTIONS.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </FilterField>
      </FilterBar>

      <Card>
        {loading ? (
          <div className="text-center py-5">
            <CmsCircularProgress />
          </div>
        ) : files.length === 0 ? (
          <Empty>No eligibility files found for this partner.</Empty>
        ) : (
          <StyledTable responsive hover className="mb-0">
            <thead>
              <tr>
                <th>File</th>
                <th>Status</th>
                <th>Template</th>
                <th>Headers</th>
                <th>Created</th>
                <th>Results</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.s3_path ? (
                      <a href={row.s3_path} target="_blank" rel="noreferrer">
                        {row.file_name}
                      </a>
                    ) : (
                      row.file_name
                    )}
                  </td>
                  <td>
                    <Badge bg={syncBadge(row.sync)}>{syncLabel(row.sync)}</Badge>
                  </td>
                  <td title={row.welcome_email_slug || DEFAULT_WELCOME_SLUG}>
                    {templateNameBySlug(row.welcome_email_slug || DEFAULT_WELCOME_SLUG)}
                  </td>
                  <td>{row.with_headers ? "Yes" : "No"}</td>
                  <td className="text-nowrap">{formatDate(row.created)}</td>
                  <td>
                    {(row.results || []).length === 0
                      ? "—"
                      : row.results.map((r) => (
                          <div key={r.id}>
                            <a href={r.s3_path} target="_blank" rel="noreferrer">
                              Result CSV
                            </a>
                          </div>
                        ))}
                  </td>
                  <td className="text-nowrap">
                    <ActionButton
                      size="sm"
                      variant="outline-secondary"
                      className="me-1"
                      onClick={() => {
                        setLogsFile({ id: row.id, name: row.file_name });
                        setLogsOpen(true);
                      }}
                    >
                      Logs
                    </ActionButton>
                    {row.sync === "failed" ? (
                      <ActionButton
                        size="sm"
                        variant="outline-primary"
                        disabled={retryingId === row.id}
                        onClick={() => handleRetry(row)}
                      >
                        {retryingId === row.id ? "…" : "Retry"}
                      </ActionButton>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        )}

        <PaginationFooter>
          <PageMeta>
            Page {page} of {totalPages}
          </PageMeta>
          <PagerButtons>
            <ActionButton
              size="sm"
              variant="outline-secondary"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </ActionButton>
            <ActionButton
              size="sm"
              variant="outline-secondary"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </ActionButton>
          </PagerButtons>
        </PaginationFooter>
      </Card>

      <UploadModal
        show={uploadOpen}
        onHide={() => setUploadOpen(false)}
        partnerId={partnerId}
        onSuccess={loadFiles}
        templates={templates}
      />
      <LogsModal
        show={logsOpen}
        onHide={() => setLogsOpen(false)}
        partnerId={partnerId}
        fileId={logsFile.id}
        fileName={logsFile.name}
      />
    </PageWrap>
  );
}

/* login.css sets global `button { width: 100% }` — override here */
const buttonReset = `
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: auto !important;
  margin: 0 !important;
  padding: 0.35rem 0.85rem !important;
  border-radius: 6px !important;
`;

const PageWrap = styled.div`
  padding: 20px 24px 28px;
  width: 100%;
  max-width: none;
  box-sizing: border-box;

  button {
    ${buttonReset}
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;

  h2 {
    margin: 0 0 4px;
    font-size: 1.45rem;
    font-weight: 600;
    color: #111827;
  }

  p {
    margin: 0;
    color: #6b7280;
    font-size: 0.925rem;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: flex-end;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 14px;
`;

const FilterField = styled.div`
  flex: ${(p) => (p.$narrow ? "0 0 180px" : "1 1 280px")};
  max-width: ${(p) => (p.$narrow ? "220px" : "480px")};

  label {
    display: block;
    margin-bottom: 4px;
    font-size: 0.8rem;
    font-weight: 500;
    color: #374151;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
`;

const StyledTable = styled(Table)`
  margin-bottom: 0;
  width: 100%;

  thead th {
    background: #f3f4f6;
    color: #374151;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
    padding: 10px 14px;
  }

  tbody td {
    padding: 12px 14px;
    vertical-align: middle;
    font-size: 0.9rem;
    border-color: #f3f4f6;
  }

  a {
    color: #2563eb;
    text-decoration: none;
    word-break: break-all;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Empty = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: #6b7280;
`;

const PaginationFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-top: 1px solid #e5e7eb;
  background: #fafafa;
`;

const PageMeta = styled.span`
  font-size: 0.85rem;
  color: #6b7280;
`;

const PagerButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled(Button)`
  ${buttonReset}
`;

/* login.css sets global `button { width: 100% }` and modals portal outside PageWrap */
const StyledModal = styled(Modal)`
  .modal-content {
    border: 0;
    border-radius: 12px;
    box-shadow: 0 18px 48px rgba(15, 23, 42, 0.18);
    overflow: hidden;
  }

  .modal-header {
    padding: 18px 20px 14px;
    border-bottom: 1px solid #eef2f7;
    align-items: flex-start;
  }

  .modal-header .modal-title {
    margin-bottom: 2px;
  }

  .modal-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #111827;
  }

  .modal-body {
    padding: 18px 20px 8px;
    outline: none;
  }

  .modal-body:focus,
  .modal-body:focus-visible {
    outline: none;
    box-shadow: none;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 20px 18px;
    border-top: 1px solid #eef2f7;
    background: #fafbfc;
  }

  .modal-footer .btn,
  .modal-header .btn-close {
    width: auto !important;
    margin: 0 !important;
  }

  .modal-footer .btn {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    min-width: 96px;
    padding: 0.45rem 1rem !important;
    border-radius: 8px !important;
    font-weight: 500;
  }

  .form-select,
  .form-control {
    border-radius: 8px;
    border-color: #d1d5db;
    min-height: 42px;
  }

  .form-select:focus,
  .form-control:focus {
    border-color: #93c5fd;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

const ModalIntro = styled.p`
  margin: 0 0 18px;
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.45;
`;

const FieldBlock = styled.div`
  margin-bottom: 16px;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
`;

const FieldHint = styled.small`
  display: block;
  margin-top: 6px;
  color: #6b7280;
  font-size: 0.8rem;
`;

const DropZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 112px;
  padding: 18px 16px;
  border: 1.5px dashed ${(p) => (p.$active ? "#3b82f6" : p.$hasFile ? "#86efac" : "#cbd5e1")};
  border-radius: 10px;
  background: ${(p) =>
    p.$active ? "rgba(59, 130, 246, 0.06)" : p.$hasFile ? "rgba(34, 197, 94, 0.06)" : "#f8fafc"};
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.65 : 1)};
  text-align: center;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${(p) => (p.$disabled ? undefined : "#3b82f6")};
    background: ${(p) => (p.$disabled ? undefined : "rgba(59, 130, 246, 0.04)")};
  }
`;

const DropTitle = styled.div`
  font-size: 0.92rem;
  font-weight: 600;
  color: #111827;
  word-break: break-all;
`;

const DropHint = styled.div`
  font-size: 0.8rem;
  color: #6b7280;
`;

const OptionRow = styled.div`
  margin: 4px 0 12px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;

  .form-check {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: auto;
  }

  .form-check-label {
    font-size: 0.9rem;
    color: #374151;
    font-weight: 500;
  }
`;

const LogsFileName = styled.div`
  max-width: 520px;
  margin-top: 2px;
  color: #6b7280;
  font-size: 0.82rem;
  font-weight: 400;
  line-height: 1.35;
  word-break: break-all;
`;

const LogsEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 160px;
  padding: 24px 16px;
  text-align: center;
`;

const LogsEmptyTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
`;

const LogsEmptyHint = styled.div`
  font-size: 0.85rem;
  color: #6b7280;
`;

const LogsTableWrap = styled.div`
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
`;

const LogsTableScroll = styled.div`
  max-height: 52vh;
  overflow: auto;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const LogsTable = styled.table`
  width: 100%;
  margin: 0;
  border: 0;
  border-collapse: separate;
  border-spacing: 0;
  background: #fff;

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8fafc;
    color: #6b7280;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    border: 0;
    border-bottom: 1px solid #e5e7eb;
    padding: 10px 14px;
    white-space: nowrap;
  }

  tbody td {
    padding: 12px 14px;
    vertical-align: top;
    font-size: 0.875rem;
    color: #111827;
    border: 0;
    border-bottom: 1px solid #eef2f7;
    background: #fff;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  tbody tr:hover td {
    background: #fafbfc;
  }

  td.time {
    white-space: nowrap;
    color: #6b7280;
    font-variant-numeric: tabular-nums;
    width: 1%;
  }

  td.message {
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.45;
  }
`;const LevelBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  background: ${(p) =>
    p.$tone === "error"
      ? "#fef2f2"
      : p.$tone === "warn"
        ? "#fffbeb"
        : p.$tone === "success"
          ? "#ecfdf5"
          : "#f1f5f9"};
  color: ${(p) =>
    p.$tone === "error"
      ? "#b91c1c"
      : p.$tone === "warn"
        ? "#b45309"
        : p.$tone === "success"
          ? "#047857"
          : "#475569"};
`;
